import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Search, Users, List } from "lucide-react";

const MASTER_TYPES = [
  ["verticals", "Verticals"], ["services", "Services"], ["products", "Products"], ["courses", "Courses"],
  ["campaigns", "Campaigns"], ["platforms", "Platforms"], ["audience_types", "Audience Types"],
  ["creative_types", "Creative Types"], ["goal_types", "Goal Types"],
];
const STATUS = ["Active", "Inactive", "Exit"];
const EMP_EMPTY = { name: "", role: "Performance Marketer", department: "Marketing", branch: "", status: "Active", joining_date: "", exit_date: "" };

function EmployeeMaster({ isAdmin }) {
  const [emps, setEmps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMP_EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/mgmt/employees", { params: { status: statusFilter, q } });
    setEmps(data);
  }, [statusFilter, q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) await api.put(`/mgmt/employees/${editId}`, form);
      else await api.post("/mgmt/employees", form);
      toast.success(editId ? "Employee updated" : "Employee added"); setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inp = "bg-[#0F172A] border-white/10 text-white h-10 focus-visible:ring-[#38BDF8]";
  const badge = (s) => s === "Active" ? "bg-[#10B981]/15 text-[#10B981]" : s === "Exit" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-slate-500/15 text-slate-400";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="emp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee" className="h-9 w-48 pl-8 bg-[#0F172A] border-white/10 text-white text-xs rounded-full" /></div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="emp-status-filter" className="h-9 w-32 bg-[#0F172A] border-white/10 text-slate-200 rounded-full text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200"><SelectItem value="all">All Status</SelectItem>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {isAdmin && <Button data-testid="add-employee-btn" onClick={() => { setForm(EMP_EMPTY); setEditId(null); setOpen(true); }} className="h-9 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full text-xs"><Plus className="w-4 h-4 mr-1.5" />Add Employee</Button>}
      </div>
      <div className="rounded-xl bg-[#1E293B] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
            <th className="text-left py-2.5 px-3">Code</th><th className="text-left py-2.5 px-3">Name</th><th className="text-left py-2.5 px-3">Role</th>
            <th className="text-left py-2.5 px-3">Branch</th><th className="text-left py-2.5 px-3">Joined</th><th className="text-center py-2.5 px-3">Status</th>{isAdmin && <th className="py-2.5 px-3"></th>}</tr></thead>
          <tbody>
            {emps.map((e) => (
              <tr key={e.id} className="border-b border-white/5">
                <td className="py-2.5 px-3 text-slate-400 text-xs">{e.employee_code}</td>
                <td className="py-2.5 px-3 text-slate-200 font-medium">{e.name}</td>
                <td className="py-2.5 px-3 text-slate-300">{e.role}</td>
                <td className="py-2.5 px-3 text-slate-300">{e.branch}</td>
                <td className="py-2.5 px-3 text-slate-400 text-xs">{e.joining_date || "—"}</td>
                <td className="py-2.5 px-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${badge(e.status)}`}>{e.status}</span></td>
                {isAdmin && <td className="py-2.5 px-3 text-right"><button data-testid={`edit-emp-${e.id}`} onClick={() => { setForm({ ...EMP_EMPTY, ...e }); setEditId(e.id); setOpen(true); }} className="text-[#38BDF8] hover:opacity-70 text-xs">Edit</button></td>}
              </tr>
            ))}
            {emps.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">No employees found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2"><Label className="text-xs text-slate-400">Name</Label><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Branch</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="emp-status" className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Joining Date</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Exit Date</Label><Input type="date" value={form.exit_date} onChange={(e) => setForm({ ...form, exit_date: e.target.value })} className={inp} /></div>
          </div>
          <DialogFooter><Button data-testid="save-employee-btn" onClick={save} className="w-full h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GenericMaster({ type, isAdmin }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(async () => { const { data } = await api.get(`/mgmt/masters/${type}`); setItems(data); }, [type]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!name.trim()) return; try { await api.post(`/mgmt/masters/${type}`, { name }); setName(""); toast.success("Added"); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (id) => { try { await api.delete(`/mgmt/masters/${type}/${id}`); await load(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex gap-2">
          <Input data-testid={`master-input-${type}`} value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${type.replace("_", " ")}`} className="h-10 bg-[#0F172A] border-white/10 text-white flex-1 max-w-sm" />
          <Button data-testid={`master-add-${type}`} onClick={add} className="h-10 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full px-5"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F172A] border border-white/10 text-sm text-slate-200">
            {it.name}
            {isAdmin && <button data-testid={`master-del-${it.id}`} onClick={() => del(it.id)} className="text-[#EF4444] hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /></button>}
          </span>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm">No items yet.</p>}
      </div>
    </div>
  );
}

export default function MasterData() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mtype, setMtype] = useState("verticals");
  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Master Data" subtitle="Control every dropdown across the system — employees, verticals, services, campaigns, platforms & more" />
      <Tabs defaultValue="employees">
        <TabsList className="bg-[#1E293B] border border-white/10">
          <TabsTrigger value="employees" data-testid="master-tab-employees" className="data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]"><Users className="w-4 h-4 mr-1.5" />Employees</TabsTrigger>
          <TabsTrigger value="masters" data-testid="master-tab-masters" className="data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]"><List className="w-4 h-4 mr-1.5" />Other Masters</TabsTrigger>
        </TabsList>
        <TabsContent value="employees" className="mt-5"><EmployeeMaster isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="masters" className="mt-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {MASTER_TYPES.map(([k, l]) => (
              <button key={k} data-testid={`master-type-${k}`} onClick={() => setMtype(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mtype === k ? "bg-[#38BDF8] text-[#0F172A]" : "bg-[#1E293B] border border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6"><GenericMaster type={mtype} isAdmin={isAdmin} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
